import type {
	PRIORITY_SCHEME_DESCRIPTION_FIELD,
	PRIORITY_SCHEME_NAME_FIELD,
	PRIORITY_SCHEME_PRIORITIES_FIELD,
	PRIORITY_SCHEME_PROJECTS_FIELD,
	PRIORITY_SCHEME_MAPPINGS_FIELD,
	INIT,
	SAVING,
	FAILURE,
	DEFAULT,
} from './constants';

/**
 * Absolute URL of the icon for the project
 */
export interface AvatarSizes {
	['16x16']: string;
	['24x24']: string;
	['32x32']: string;
	['48x48']: string;
}

export interface Priority {
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	id: string;
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * Always be at least one default priority per priority scheme. Users can edit which priority is the
	 * default for a scheme. This priority may or may not be default for other schemes.
	 */
	isDefault: boolean;
	/**
	 * Absolute url
	 */
	iconUrl: string;
	/**
	 * Priority order is inherited from the global priority list.
	 */
	sequence: number;
}
/**
 * Same as {@link Priority} but does not contain {@link Priority.isDefault}
 * It is used for priority picker
 */
export interface PriorityOption {
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	id: string;
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * Absolute url
	 */
	iconUrl: string;
	/**
	 * Priority order is inherited from the global priority list.
	 */
	sequence: number;
}

/**
 * The priority type that is returned by the backend APIs.
 *
 * This is the priority type returned from get priorities etc
 *
 * Note that this is the type returned for the values for getting
 * the suggested mappings
 */
export interface PriorityOnServer {
	/**
	 * Link to priority
	 */
	self: string;
	/**
	 * Status color of the priority, set in hexademical
	 */
	statusColor: string;
	/**
	 * Can be empty string, user generated content
	 */
	description: string;
	/**
	 * Absolute URL
	 */
	iconUrl: string;
	/**
	 * Cannot be empty string, user generated content
	 */
	name: string;
	/**
	 * Arbitrary, safe and unique for jira instance
	 */
	id: string;
	/**
	 * For mappings this will always be false.
	 */
	isDefault: boolean;
	/**
	 * Priority order is inherited from the global priority list.
	 */
	sequence: string;
}

export interface Project {
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	id: string;
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * Absolute URL of the icon for the project
	 */
	avatarUrl: string;
}

export interface CreatePrioritySchemeProjectsFieldValue {
	projectIds?: string[];
}

export interface EditPrioritySchemeProjectsFieldValue {
	projects: {
		/**
		 * User generated content, if null no projects will be added to the scheme.
		 */
		add?: {
			/**
			 * ids are unique and safe for jira instance.
			 */
			ids: string[];
		};
		/**
		 * User generated content, if null no projects will be removed from the scheme.
		 */
		remove?: {
			/**
			 * ids are unique and safe for jira instance.
			 */
			ids: string[];
		};
	};
}

export type ProjectsFieldValue =
	| CreatePrioritySchemeProjectsFieldValue
	| EditPrioritySchemeProjectsFieldValue;

interface PrioritySchemeFields {
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * User generated content
	 */
	description: string;
	/**
	 * There can only be one default scheme. Users cannot change the name or description of the default scheme.
	 * Users change or delete the default scheme.
	 */
	isDefault: boolean;
	/**
	 *  Priority order is inherited from the global priority list.
	 *  More info: https://hello.atlassian.net/wiki/spaces/MOCA/pages/2581687264/LDR+-+P+3+-+Should+we+order+the+priorities+in+schemes+by+the+order+in+the+global+list
	 *  Priority schemes must contain at least one priority, thus this list can only be empty when first creating
	 *  a new scheme.
	 */
	priorities: Priority[];
	/**
	 * Projects are paginated. Empty list means that this scheme is not associated with any project.
	 */
	projects: {
		total: number;
		values: Project[];
	};
}

export type CreatePriorityScheme = PrioritySchemeFields & {
	/**
	 * No ID value exists while creating a new priority scheme
	 * An ID value will be populated after the priority scheme has been successfully created
	 */
	id: undefined;
};

export type PriorityScheme = PrioritySchemeFields & {
	/**
	 * Arbitrary, safe and unique for priority schemes in jira instance.
	 */
	id: string;
};

export type CreateOrEditPriorityScheme = CreatePriorityScheme | PriorityScheme;

export interface PrioritySchemeFormValues {
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * User generated content
	 */
	description: string;
}

export type PrioritySchemeFieldNames =
	| typeof PRIORITY_SCHEME_NAME_FIELD
	| typeof PRIORITY_SCHEME_DESCRIPTION_FIELD
	| typeof PRIORITY_SCHEME_PRIORITIES_FIELD
	| typeof PRIORITY_SCHEME_PROJECTS_FIELD
	| typeof PRIORITY_SCHEME_MAPPINGS_FIELD;

export interface AsyncTask {
	task: {
		/**
		 * Arbitrary, safe and unique id for jira instance
		 */
		id: string;
		/**
		 * href to async task, will appear in form /rest/api/3/task/{id}
		 */
		self: string;
	};
}

export interface FlagData {
	/**
	 * User generated content
	 */
	schemeName: string;
	/**
	 * Reference to {@link PriorityScheme.id}
	 */
	schemeId: string;
}

export type PrioritySchemeFormState = typeof INIT | typeof SAVING | typeof FAILURE | typeof DEFAULT;

export interface PrioritySchemeValidationError {
	field: PrioritySchemeFieldNames;
	message: string;
}

export interface PriorityMapping {
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	fromPriorityId: number;
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	toPriorityId: number;
}

export interface ProjectMapping {
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	fromProjectId: number;
	/**
	 * Arbitrary, safe and unique id for jira instance
	 */
	toProjectId: number;
}

export const ModalOnCompleteStatus = {
	DISMISSAL: 'DISMISSAL',
	ERROR: 'ERROR',
	SUCCESS: 'SUCCESS',
} as const;

export interface PrioritySchemeModalWrapperProps {
	isOpen: boolean;
	/**
	 * Callback will be called when users are done interacting with the form.
	 */
	onComplete: (status: (typeof ModalOnCompleteStatus)[keyof typeof ModalOnCompleteStatus]) => void;
	/**
	 * If null, priority scheme state is set as empty scheme , if not priority scheme state is initialised
	 * using this. This will not be updated.
	 */
	initialPriorityScheme?: CreateOrEditPriorityScheme;
	/**
	 * 0 indexed representation of modal tabs, will default to 0 unless specified
	 */
	startingTab?: number;
}

export type DeleteSchemeModalProps =
	| {
			isOpen: false;
	  }
	| {
			isOpen: true;
			isSubmitting: boolean;
			schemeId: PriorityScheme['id'];
			schemeName: PriorityScheme['name'];
			associatedProjectCount: number;
	  };

export interface Page<T> {
	/**
	 * The current page number. Page numbering starts from 1.
	 * */
	currentPage: number;
	/**
	 * The items on the page.
	 * */
	items: T[];
	/**
	 * The total number of items.
	 * */
	totalItems: number;
	/**
	 * The number of items per page.
	 * */
	pageSize: number;
}

export const ProjectFilterChoice = {
	/**
	 * Default filter choice for the Project Picker component
	 * Shows a list of projects available in the user's Jira instance
	 */
	ALL_PROJECTS: 'All projects',
	/**
	 * Secondary filter choice for the Project Picker component
	 * Shows a list of projects which are already associated to the current priority scheme
	 */
	ASSOCIATED_PROJECTS: 'Existing projects',
} as const;

export interface ProjectPickerFilter {
	projectFilterChoice: (typeof ProjectFilterChoice)[keyof typeof ProjectFilterChoice];
	query: string;
}

export interface OnFetchParam {
	/**
	 * Filter choice for the Project Picker component to retrieve all projects in Jira or associated projects with the current priority scheme
	 */
	projectFilterChoice?: (typeof ProjectFilterChoice)[keyof typeof ProjectFilterChoice];
	/**
	 * Search query for the Project Picker component to find projects
	 */
	query?: string;
	/**
	 * Starting page for retrieving a list of paginated projects
	 */
	selectedPage?: number;
}

export const ProjectStyleForInput = {
	// "Company-Managed Projects"
	CMP: 'classic',
	// "Team-Managed Projects"
	TMP: 'next-gen',
};

export interface ProjectInput {
	/**
	 * Absolute URL for a project's avatar
	 */
	avatarUrl: string;
	/**
	 * Whether a project is selected or not
	 */
	checked: boolean;
	/**
	 * Whether a project input option is disabled or not
	 */
	disabled: boolean;
	/**
	 *  Arbitrary, safe and unique ID for a project in the Jira instance
	 */
	id: string;
	/**
	 * Whether the project is currently associated with the priority scheme
	 */
	isAssociatedWithScheme: boolean;
	/**
	 * User generated content
	 */
	name: string;
	/**
	 * Project style for a Jira project
	 */
	projectStyle: (typeof ProjectStyleForInput)[keyof typeof ProjectStyleForInput];
}

/**
 * Transformed data returned from API endpoints for the Project Picker component
 */
export interface RemoteDataForProjectPicker {
	/**
	 * Number of projects available in the Jira instance which changes based on query results
	 */
	countForQueriedProjectsInJira: number;
	/**
	 * Number of projects already associated with the current priority scheme which changes based on query results
	 */
	countForQueriedAssociatedProjects: number;
	/**
	 * Number of projects which are currently associated with the priority scheme in the Jira database
	 */
	totalNumberOfAssociatedProjects: number;
	priorityScheme: {
		/**
		 * User generated content for the name of a priority scheme
		 */
		name: string;
		/**
		 * Whether a priority scheme is the default priority scheme in the Jira instance
		 */
		isDefault: boolean;
	};
	/**
	 * List of all projects available in Jira which may or may not be associated with a priority scheme already
	 */
	projects: ProjectInput[];
}

export const ProjectPickerState = {
	/**
	 * On the first page load, before any requests begin, the state is "INITIAL"
	 */
	INITITAL: 'INITIAL',
	ERROR: 'ERROR',
	LOADING: 'LOADING',
	SUCCESS: 'SUCCESS',
} as const;

export const PrioritySchemesPageState = {
	/**
	 * On the first page load, before any requests begin, the state is "INITIAL"
	 */
	INITITAL: 'INITIAL',
	ERROR: 'ERROR',
	LOADING: 'LOADING',
	SUCCESS: 'SUCCESS',
} as const;

export type ProjectPickerPage = Page<ProjectInput>;
export type PrioritySchemesPage = Page<PriorityScheme>;

export interface OnEditSchemeParam {
	prioritySchemeId: string;
	startingTab: number;
}

/**
 * Represents a paginated list of priorities that must be mapped
 */
export type RequiredMapping = {
	/**
	 * Unique and safe priority scheme id, represents the priority scheme that is
	 * being edited or created.
	 */
	fromSchemeId: string;
	/**
	 * Unique and safe priority scheme id, represents the priority scheme that the
	 * priorities are getting mapped to.
	 */
	toSchemeId: string | null;
	/**
	 * Represents the list of priorities that need to be mapped.
	 */
	fromPriorities: Priority[];
};

export type PriorityMappings = Record<
	/**
	 * Priority id that is being mapped to new priority, unique and safe
	 */
	string,
	/**
	 * Priority to be mapped to, store the object to render in dropdown
	 */
	Priority
>;

/**
 * Type used for mapping in the create and edit API requests
 */
export type RequestMappings = {
	/**
	 * Mappings for priorities for the current scheme. i.e. priorities coming into the current scheme
	 */
	in: Record<number, number>;
	/**
	 * Mappings for priorities for the default scheme. i.e. priorities leaving the current scheme -> default scheme
	 */
	out: Record<number, number>;
};

/**
 * Type to track the diffs of priorities and projects in a scheme.
 */
export type MappingsDiffs = {
	prioritiesDiff: {
		/**
		 * List of priority ids, safe and unique for the current instance.
		 */
		add: string[];
		/**
		 * List of priority ids, safe and unique for the current instance.
		 */
		remove: string[];
	};
	projectsDiff: {
		/**
		 * List of project ids, safe and unique for the current instance.
		 */
		add: string[];
		/**
		 * List of projects ids, safe and unique for the current instance.
		 */
		remove: string[];
	};
};
